import { chain } from 'obliterator';
import { BoxHelper, Group, Matrix4, Vector3 } from 'three';
import type {
  Box3,
  LineBasicMaterial,
  MeshPhysicalMaterialParameters,
} from 'three';

import type {
  CompositeProgressController,
  AnyProgressSignal,
} from '@frontend/controllers';
import {
  addEventListenerAndCollectRemovers,
  runRemoveEventListeners,
} from '@frontend/utils';

import { PART_TYPES, ENTITY_TYPES, ActiveRepresentation } from '../constants';
import type { BodyJson } from '../contracts/body';
import {
  getMidpoint,
  isIdentityTransformArray,
  identityTransform,
  disposeThreeJS,
} from '../geom-utils';
import { disposeAdditionalFeedback } from '../renderer/creation-utils';
import { createFlatPartEdgeLineSegments2 } from '../renderer/flat-part-line-mesh';
import type {
  AsyncLoadable,
  SyncLoadable,
} from '../renderer/loadingPriorityQueue';
import type {
  HasRenderable,
  HasRenderableWithGeometry,
  Renderer,
} from '../renderer/renderer';
import { viewerDataService } from '../viewerDataService';
import { OBBSimplifiedRepresentation } from './OBBSimplifiedRepresentation';
import { OptimalBoundingBox } from './OptimalBoundingBox';
import type { AssemblyInstance } from './assemblyInstance';
import type { Part } from './part';
import { STEPBody } from './step/STEPBody';
import type { StepEdgeModel } from './step/StepEdgeModel';
import type { StepFaceModel } from './step/StepFaceModel';
import type { StepTextModel } from './step/StepTextModel';
import { constructEntityName } from './step/utils';
import { STLBody } from './stl/STLBody';
import { type HasMaxSideArea } from './utils';

export type BodyObjFromPartType<T extends PART_TYPES> = T extends PART_TYPES.STL
  ? STLBody
  : T extends PART_TYPES.STEP
  ? STEPBody
  : T extends PART_TYPES.DXF
  ? STEPBody
  : never;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyBody = Body<any>;

export class Body<T extends PART_TYPES>
  implements AsyncLoadable, SyncLoadable, HasRenderable, HasMaxSideArea
{
  public static readonly suffix = '-body';
  public readonly name?: string;
  public readonly id?: string;
  public readonly partUuid?: string;
  public readonly isMaterial?: boolean;
  public readonly isRoot?: boolean;
  public readonly fullDetailRep = new Group();
  public readonly group: Group = new Group(); //group for controlling visibility of body's fullDetailRep | obbSimplifiedRep
  public readonly optimalBoundingBox?: OptimalBoundingBox;
  public readonly additionalFeedbackGroup: Group = new Group();
  public readonly additionalFeedback: Record<string, Group> = {};
  public readonly specialMaterialProps?: MeshPhysicalMaterialParameters;
  public readonly initialPosition: Vector3;
  public readonly initialBoxMidpoint: Vector3 = new Vector3();

  private _obbSimplifiedRep?: OBBSimplifiedRepresentation;
  private _parent?: AssemblyInstance;
  private _activeRepresentation: ActiveRepresentation =
    ActiveRepresentation.FullDetailRep;
  private _transform?: Matrix4; //only defined if NOT identityTransform
  private _obj?: BodyObjFromPartType<T>;
  private _boundBox?: BoxHelper;
  private data?: BodyJson; //cache BodyJson until this.part.hasType is true
  private _progress?: CompositeProgressController;
  private _isLoaded?: boolean;
  private disposeFns: (() => void)[] = [];
  private isDisposed?: boolean;

  constructor(
    data: BodyJson,
    public readonly part: Part<T>,
    private renderer: Renderer,
    parentProgress?: CompositeProgressController,
    private readonly signal?: AbortSignal, //AbortSignal to abort loading
    public readonly nativeUnits?: string,
    isMaterial?: boolean,
    specialMaterialProps?: MeshPhysicalMaterialParameters,
  ) {
    this.name = data.name;
    this.id = data.id;
    this.group.name = `body|${this.id}`;
    this.additionalFeedbackGroup.name = `body|${this.id}|additionalFeedback`;
    this.partUuid = data.partFileUuid || data.part_uuid; //pcad provides part_uuid
    if (data.transform && !isIdentityTransformArray(data.transform)) {
      this._transform = new Matrix4().fromArray(data.transform); //only store if not identity
      this.group.applyMatrix4(this._transform);
      this.additionalFeedbackGroup.applyMatrix4(this._transform);
    }
    this.initialPosition = this.group.position.clone();

    this.isRoot = data.isRoot || data.is_root; //pcad provides is_root
    if (isMaterial) {
      this.isMaterial = isMaterial;
    }
    if (specialMaterialProps) {
      this.specialMaterialProps = specialMaterialProps;
    }

    this.data = data; //cached until createUnhydratedBody() knows the part.type

    this.fullDetailRep.name = `bodyFullDetailRep|${this.id}`;
    this.group.add(this.fullDetailRep);

    //Establish optimal bounding box
    if (this.part.optimalBoundingBoxGeometry) {
      this.optimalBoundingBox = new OptimalBoundingBox(
        this.part.optimalBoundingBoxGeometry,
      );
      this.optimalBoundingBox.group.applyMatrix4(this.transform);
    }

    this._progress = parentProgress?.pushCompositeProgress({
      id: viewerDataService.getBodyOrAssemblyInstanceLoadingId(
        this.id as string,
      ),
      description: `Loading Body for ${part.name || part.id} (instance ${
        this.id
      })`,
      itemAndOrWeights: [{ signal: this.part.progress }],
    });

    if (!this.part.isLoaded) {
      this._progress?.push({ signal: this.part?.progress });
    }

    this.createUnhydratedBody(); //noop if part.hasType is false. (Will be called again when part is hydrated)

    if (signal) {
      addEventListenerAndCollectRemovers(
        this.disposeFns,
        signal,
        'abort',
        () => {
          this.dispose();
        },
      );
    }
  }

  public dispose() {
    if (this.isDisposed) return;
    this.isDisposed = true;
    runRemoveEventListeners(this.disposeFns);
    delete this.data;
    this.abortLoad();
    if (this._obj) {
      this._obj.dispose();
    }
    delete this._transform;
    delete this._parent;
    if (this.optimalBoundingBox) {
      this.optimalBoundingBox.dispose();
    }
    this._obbSimplifiedRep?.dispose();
    disposeThreeJS(this._boundBox, this.fullDetailRep, this.group);

    //Note: additionalFeedback was cloned from part's additionalFeedback (establishAdditionalFeedback)
    //So it needs to be cleaned
    disposeAdditionalFeedback(this.additionalFeedback);
    this.additionalFeedbackGroup.clear();
    this.additionalFeedbackGroup.removeFromParent();
  }

  public get renderable() {
    return this.group;
  }

  public get geometryVerticesCount(): number {
    return this.obj ? this.obj.geometryVerticesCount : 0;
  }

  public get activeRepresentation(): ActiveRepresentation {
    return this._activeRepresentation;
  }

  public set activeRepresentation(v: ActiveRepresentation) {
    if (
      v === ActiveRepresentation.OBBSimplifiedRep &&
      this.optimalBoundingBox
    ) {
      if (!this._obbSimplifiedRep) {
        this.createObbSimpRep();
      }
      if (this._obbSimplifiedRep) {
        this._obbSimplifiedRep.visible = true;
        this.fullDetailRep.visible = false;
        this._activeRepresentation = v;
        this.renderer.intersectionEntities.add(
          this._obbSimplifiedRep.intersectable,
        );
      }
    } else {
      this.fullDetailRep.visible = true;
      if (this._obbSimplifiedRep) {
        this._obbSimplifiedRep.visible = false;
        this.renderer.intersectionEntities.delete(
          this._obbSimplifiedRep.intersectable,
        );
      }
      this._activeRepresentation = ActiveRepresentation.FullDetailRep;
    }
  }

  public toggleActiveRepresentation() {
    if (this._activeRepresentation === ActiveRepresentation.OBBSimplifiedRep) {
      this.activeRepresentation = ActiveRepresentation.FullDetailRep;
    } else if (
      this._activeRepresentation === ActiveRepresentation.FullDetailRep
    ) {
      this.activeRepresentation = ActiveRepresentation.OBBSimplifiedRep;
    }
  }

  public get obbMaxSideArea(): number {
    if (this.part.optimalBoundingBoxGeometry) {
      return this.part.optimalBoundingBoxGeometry.maxSideArea;
    }
    //Otherwise use axis aligned box if it exists
    if (this._boundBox) {
      const box = this._boundBox.geometry.boundingBox as Box3;
      const { x: width, y: length, z: height } = box.getSize(new Vector3());
      return [length * width, width * height, length * height]
        .map((v) => {
          const p = Math.abs(v);
          return Number.isFinite(p) ? p : 0;
        })
        .sort()[2];
    }
    return 0;
  }

  public get metaData(): Record<string, never> {
    return {};
  }

  public get transform(): Matrix4 {
    return this._transform || identityTransform;
  }

  public get parent(): AssemblyInstance | undefined {
    return this._parent;
  }

  public set parent(parent: AssemblyInstance | undefined) {
    if (parent !== this._parent) {
      if (this._parent) {
        throw new Error('Parent already set');
      }
      if (parent) {
        this._parent = parent;
      }
    }
  }

  public get boundBox(): BoxHelper {
    if (!this._boundBox) {
      throw new Error('Body has not been loaded');
    }
    return this._boundBox;
  }

  public get type(): PART_TYPES {
    return this.part.type;
  }

  public get obj(): BodyObjFromPartType<T> {
    if (!this._obj) {
      throw new Error(`Body's part has not been loaded`);
    }
    return this._obj;
  }

  public get isLoaded(): boolean {
    //true after this._obj.isLoaded and this.finalize() has finished
    return !!this._isLoaded;
  }

  public get isLoading(): boolean {
    return (this.part.isLoading || this._obj?.isLoading) ?? false;
  }

  public get progress(): AnyProgressSignal | undefined {
    return this._progress?.signal;
  }

  public pause(): void {
    if (!this.signal?.aborted) {
      if (this.part.isLoading) {
        this.part.pause();
      }
      this._obj?.pause();
    }
  }

  public get isPaused(): boolean {
    return this.part.isPaused || this._obj?.isPaused || false;
  }

  public resume(): void {
    if (!this.signal?.aborted) {
      this.part.resume();
      this._obj?.resume();
    }
  }

  public abortLoad() {
    if (this.part) {
      this.part.abortLoad();
    }
    if (this._obj) {
      this._obj.abortLoad();
    }
    if (this._progress) {
      this._progress.close();
      delete this._progress;
    }
  }

  public get hasAssemblyStructureBeenEstablished(): boolean {
    //If !root, then a parent should be defined
    return !!(this.isRoot || this._parent);
  }

  public get rootPartFileUuid(): string {
    if (!this.hasAssemblyStructureBeenEstablished) {
      throw new Error('Assembly structure has not been established.');
    }
    //eslint-disable-next-line @typescript-eslint/no-this-alias
    let node: AssemblyInstance | AnyBody = this;
    while (node.parent) {
      node = node.parent;
    }
    const partOrAssembly =
      node &&
      ((node as unknown as AnyBody).part ||
        (node as unknown as AssemblyInstance).assembly);
    return partOrAssembly?.id || '';
  }

  public get obbSimplifiedRep() {
    return this._obbSimplifiedRep;
  }

  public get rootBodyOrAssemblyInstanceId(): string {
    if (!this.hasAssemblyStructureBeenEstablished) {
      throw new Error('Assembly structure has not been established.');
    }
    //eslint-disable-next-line @typescript-eslint/no-this-alias
    let node: AssemblyInstance | AnyBody = this;
    while (node.parent) {
      node = node.parent;
    }
    return node?.id || '';
  }

  public load(): void {
    if (!this._isLoaded && !this.signal?.aborted) {
      if (!this._obj) {
        this.part.load();
        this.createUnhydratedBody();
      }
      this._obj?.load();
      this.finalize();
    }
  }

  public async loadAsyncUnhydrated(
    lastPause: number,
    parentProgress?: CompositeProgressController,
  ): Promise<number> {
    if (!this._isLoaded && !this.signal?.aborted) {
      let obj = this._obj;
      if (!obj) {
        //load the part so that the body's obj can be created
        if (!this.part.isLoaded) {
          if (parentProgress) {
            const partProgressSignal = this.part.progress;
            if (partProgressSignal) {
              parentProgress.push({ signal: partProgressSignal, weight: 1 });
            }
          }
          //eslint-disable-next-line no-param-reassign
          lastPause = await this.part.loadAsync(lastPause);
        }
        obj = this.createUnhydratedBody() as BodyObjFromPartType<T>;
      }
    }
    return lastPause;
  }

  public async loadAsync(
    lastPause: number = performance.now(),
  ): Promise<number> {
    //eslint-disable-next-line no-param-reassign
    lastPause = await this.loadAsyncUnhydrated(lastPause);
    if (!this._isLoaded && !this.signal?.aborted) {
      const obj = this._obj;
      //Load the obj
      if (obj) {
        //eslint-disable-next-line no-param-reassign
        lastPause = await obj.loadAsync(lastPause);
        this.finalize();
      }
    }
    return lastPause;
  }

  public updateTodoCount() {
    if (!this._isLoaded && !this.signal?.aborted && this.part.isLoaded) {
      let obj = this._obj;
      if (obj) {
        obj.updateTodoCount();
      } else {
        obj = this.createUnhydratedBody() as BodyObjFromPartType<T>;
      }
    }
  }

  public getEntityInformation(): {
    minRenderCount: number;
    minInteractionCount: number;
    maxRenderCount: number;
    maxInteractionCount: number;
  } {
    const obj = this._obj;
    let minRenderCount = 0;
    let maxRenderCount = 0;
    let minInteractionCount = 0;
    let maxInteractionCount = 0;
    if (obj instanceof STEPBody) {
      if (!obj?.isLoaded) {
        throw new Error('Body has not been loaded');
      }
      if (!this.hasAssemblyStructureBeenEstablished) {
        throw new Error('Assembly structure has not been established.');
      }
      const isVisible = this.isVisible;
      if (isVisible) {
        let faceLength = 0;
        for (const face of obj.faces.values()) {
          if (face.visible) {
            faceLength += 1;
          }
        }
        maxRenderCount += faceLength;
        minRenderCount += faceLength;
        maxInteractionCount += faceLength;
        minInteractionCount += faceLength;

        maxRenderCount += obj.edges.size;
        maxInteractionCount += obj.edges.size;
      }
      if (this.type === PART_TYPES.DXF && isVisible) {
        minRenderCount += obj.edges.size;
        minInteractionCount += obj.edges.size;
      }
      // TODO check if these children have more than one body like if they are pseudo steps
      if (this.additionalFeedbackGroup.visible) {
        for (const key in this.additionalFeedback) {
          const group = this.additionalFeedback[key];
          if (group.visible) {
            maxRenderCount += group.children.length;
            minRenderCount += group.children.length;
            for (const child of group.children) {
              maxRenderCount += child.children.length;
              minRenderCount += child.children.length;
            }
          }
        }
      }
    } else if (obj instanceof STLBody) {
      // TODO: better interaction analysis for stl files
      minRenderCount = 1;
      minInteractionCount = 1;
      maxInteractionCount = 1;
      maxRenderCount = 1;
    }
    return {
      minRenderCount,
      minInteractionCount,
      maxRenderCount,
      maxInteractionCount,
    };
  }

  // TODO: this recursively copies all the children of a group
  // It would be more performant to only copy meshes/materials of part additional feedback and display those
  // like we do with the part-body paradigm. This is an optimization that can be made by perhaps managing
  // additional feedback with explicit feature classes. That will allow us to get more granular info on
  // rendering demands as well.
  public establishAdditionalFeedback(): void {
    for (const key in this.part.additionalFeedback) {
      if (key in this.additionalFeedback) continue;

      const newGroup = this.part.additionalFeedback[key].clone();
      newGroup.name = `body|${this.id}|additionalFeedback|${key}`;
      this.additionalFeedback[key] = newGroup;
      this.additionalFeedbackGroup.add(newGroup);
    }
  }

  public scaleVertexes(zoomFactor: number): void {
    const obj = this._obj;
    if (obj instanceof STEPBody) {
      if (!obj?.isLoaded) {
        throw new Error('Body has not been loaded');
      }
      obj.scaleVertexes(zoomFactor);
    }
  }

  public translate(v: Vector3): void {
    const obj = this._obj;
    this.group.position.add(v);
    this.additionalFeedbackGroup.position.add(v);

    if (obj instanceof STEPBody) {
      const { faces, edges, texts, vertices } = obj;
      for (const renderable of chain<
        | HasRenderableWithGeometry
        | StepEdgeModel
        | StepTextModel
        | StepFaceModel
      >(faces.values(), edges.values(), texts.values(), vertices.values())) {
        renderable.translate(v);
      }
    } else if (obj instanceof STLBody) {
      obj.translateFullDetailRepBox(v);
    }

    if (this.optimalBoundingBox) {
      this.optimalBoundingBox.group.position.add(v);
    }
  }

  public getExplodeTranslationVector(
    parentBoxCenter: Vector3,
    factor: number,
  ): Vector3 {
    // based on parent bounding box center and our initial center, calculate the translation vector
    // for the body given zero state. Assumes the factor is scalar multiple of original center to center length
    return new Vector3()
      .subVectors(this.initialBoxMidpoint, parentBoxCenter)
      .multiplyScalar(factor);
  }

  public createUnhydratedBody(): BodyObjFromPartType<T> | undefined {
    if (!this._obj && this.part.hasType && this.data) {
      const [obj, objProgress] =
        this.part.type === PART_TYPES.STEP || this.part.type === PART_TYPES.DXF
          ? STEPBody.factory(
              this.data,
              this.part as Part<PART_TYPES.STEP>,
              this as Body<PART_TYPES.STEP>,
              this.signal,
            )
          : STLBody.factory(
              this.data,
              this.part as Part<PART_TYPES.STL>,
              this as Body<PART_TYPES.STL>,
              this.signal,
            );
      this._progress?.push({ signal: objProgress });
      this._progress?.close();
      this._obj = obj as BodyObjFromPartType<T>;
      delete this.data;
    } else {
      this.updateTodoCount();
    }
    return this._obj;
  }

  //obbSimplifiedRep is created on demand for performance reasons.
  //The vast majority of time we never need it... and when we do need it, there tends to be a lot of components so creating it slows things down
  private createObbSimpRep(): OBBSimplifiedRepresentation | undefined {
    if (this.optimalBoundingBox && this.part.optimalBoundingBoxGeometry) {
      this._obbSimplifiedRep = new OBBSimplifiedRepresentation(
        this.part.optimalBoundingBoxGeometry,
        constructEntityName(
          this.id ?? 'undefined',
          ENTITY_TYPES.SimpRep,
          'obb',
        ),
        this,
      );
      if (!this._isLoaded) {
        //_boundBox is initially based on obbSimplifiedRepsooner but will be replaced
        this._boundBox = new BoxHelper(this._obbSimplifiedRep.intersectable);
        (this._boundBox.material as LineBasicMaterial).linewidth = 2;
        this._boundBox.visible = false;
        this._boundBox.geometry.computeBoundingBox();
        const box = this._boundBox.geometry.boundingBox as Box3;
        this.initialBoxMidpoint.copy(getMidpoint(box.min, box.max));
      }
    }
    return this._obbSimplifiedRep;
  }

  private finalize(): void {
    if (!this._isLoaded && !this.signal?.aborted) {
      const obj = this._obj;
      if (!obj?.isLoaded) {
        throw new Error('Body has not been loaded');
      }
      // DXF files use "fat lines" (LineSegments2) for consistent line thickness at any zoom level.
      // This creates the fat lines from edge geometries, adds them to the scene, and hides the
      // original thin LineSegments to prevent double-rendering.
      if (this.part.type === PART_TYPES.DXF && obj instanceof STEPBody) {
        const flatPartLines = createFlatPartEdgeLineSegments2(
          obj.edgeGeometries,
        );
        obj.edgeLineSegments2 = flatPartLines;
        const edgeChildCountBefore = obj.edgesGroup.children.length;
        for (const lineSegments2 of flatPartLines) {
          obj.edgesGroup.add(lineSegments2);
        }
        for (let i = 0; i < edgeChildCountBefore; i++) {
          obj.edgesGroup.children[i].visible = false;
        }
      }
      this.establishAdditionalFeedback();
      if (this._transform) {
        //transform renderable boundBoxes if this._transform is defined (ie it is not identity matrix)
        if (obj instanceof STLBody) {
          obj.boundingBox.applyMatrix4(this._transform);
        } else {
          for (const edgeOrFace of chain<
            StepFaceModel | StepEdgeModel | StepTextModel
          >(obj.faces.values(), obj.edges.values(), obj.texts.values())) {
            edgeOrFace.boundBox?.applyMatrix4(this._transform);
          }
        }
      }

      //Establish Bound Box
      if (this._boundBox) {
        this._boundBox.copy(new BoxHelper(this.fullDetailRep));
        this._boundBox.geometry.boundingBox = null; //need to recompute
      } else {
        this._boundBox = new BoxHelper(this.fullDetailRep);
      }
      (this._boundBox.material as LineBasicMaterial).linewidth = 2;
      this._boundBox.visible = false;

      this._boundBox.geometry.computeBoundingBox();

      //See note in STEPBody.ts. The vertex must be added to
      //this.group AFTER the boundBox is established (which is done in lines above).
      if (obj instanceof STEPBody) {
        for (const vertexBody of obj.vertices.values()) {
          this.fullDetailRep.add(vertexBody.vertex);
        }
        this.fullDetailRep.add(obj.textsGroup);
        //Update each text boundBox after it is added to this.group
        obj.textsGroup.updateMatrixWorld();
        for (const text of obj.texts.values()) {
          text.boundBox.applyMatrix4(text.matrixWorld);
        }
      }

      // now get positional info
      const box = this._boundBox.geometry.boundingBox as Box3;
      this.initialBoxMidpoint.copy(getMidpoint(box.min, box.max));
      if (this._progress) {
        this._progress.close(); //should already be closed (but just in case)
        this._progress.disposeIfUnused();
        delete this._progress;
      }
      this._isLoaded = true;
    }
  }

  private get isVisible(): boolean {
    //Important: The following should be checked for this call to be successful
    //* It is already being checked before accessing private this.isVisible
    // if (!this._isLoaded) {
    //   throw new Error('Body has not been loaded');
    // }
    // if (!this.hasAssemblyStructureBeenEstablished) {
    //   throw new Error('Assembly structure has not been established.');
    // }
    const group = this.group;
    let parent = group.parent;
    let isVisible = group.visible;
    while (isVisible && parent) {
      isVisible = parent.visible;
      parent = parent.parent;
    }
    return isVisible;
  }
}

export default Body;
